/**
 * Outil « Machine à nombres » — calculs (pur).
 *
 * Un programme de calcul est une suite d'étapes « ajouter / soustraire /
 * multiplier par / diviser par » un OPÉRANDE, qui est un nombre, un multiple
 * du nombre de départ (« le double du nombre de départ ») ou son carré, plus
 * « élever au carré ». On l'exécute sur un nombre (fractions exactes) ou sur
 * x : chaque étape écrit l'expression obtenue à partir de la forme réduite
 * précédente, puis la réduit (polynôme en x à coefficients rationnels).
 */

import { add, div, frac, mul, sub, type Frac } from '../../ui/exact';

/** polynôme : coefficient de xᵏ à l'indice k */
export type Poly = Frac[];

export type Operand = { kind: 'const'; k: number } | { kind: 'x'; k: number } | { kind: 'x2' };

export type Step =
  | { op: 'add' | 'sub' | 'mul'; operand: Operand }
  | { op: 'div'; k: number }
  | { op: 'square' };

export interface Program {
  id: string;
  label: string;
  steps: Step[];
}

const c = (k: number): Operand => ({ kind: 'const', k });
const X = (k = 1): Operand => ({ kind: 'x', k });

export const PROGRAMS: readonly Program[] = [
  {
    id: 'double',
    label: '+3, ×2, −6',
    steps: [
      { op: 'add', operand: c(3) },
      { op: 'mul', operand: c(2) },
      { op: 'sub', operand: c(6) },
    ],
  },
  {
    id: 'four',
    label: 'Toujours 4 ?',
    steps: [
      { op: 'mul', operand: c(4) },
      { op: 'add', operand: c(8) },
      { op: 'div', k: 2 },
      { op: 'sub', operand: X(2) },
    ],
  },
  {
    id: 'square',
    label: 'Avec un carré',
    steps: [{ op: 'add', operand: c(1) }, { op: 'square' }, { op: 'sub', operand: { kind: 'x2' } }],
  },
  {
    id: 'times',
    label: '×3, +5',
    steps: [
      { op: 'mul', operand: c(3) },
      { op: 'add', operand: c(5) },
    ],
  },
  {
    id: 'twoplus',
    label: '×2, +1',
    steps: [
      { op: 'mul', operand: c(2) },
      { op: 'add', operand: c(1) },
    ],
  },
];

export const MAX_STEPS = 6;

// ── polynômes ───────────────────────────────────────────────────────────────

const ZERO = frac(0);

export function trim(p: Poly): Poly {
  const out = [...p];
  while (out.length > 1 && out[out.length - 1]!.p === 0) out.pop();
  return out.length ? out : [ZERO];
}

export function polyAdd(a: Poly, b: Poly): Poly {
  const n = Math.max(a.length, b.length);
  return trim(Array.from({ length: n }, (_, i) => add(a[i] ?? ZERO, b[i] ?? ZERO)));
}

export function polyNeg(a: Poly): Poly {
  return a.map((x) => frac(-x.p, x.q));
}

export function polyMul(a: Poly, b: Poly): Poly {
  const out: Poly = Array.from({ length: a.length + b.length - 1 }, () => ZERO);
  a.forEach((x, i) => b.forEach((y, j) => (out[i + j] = add(out[i + j]!, mul(x, y)))));
  return trim(out);
}

export function degree(p: Poly): number {
  const t = trim(p);
  return t.length === 1 && t[0]!.p === 0 ? -Infinity : t.length - 1;
}

export function operandPoly(o: Operand): Poly {
  if (o.kind === 'const') return [frac(o.k)];
  if (o.kind === 'x') return [ZERO, frac(o.k)];
  return [ZERO, ZERO, frac(1)];
}

export function applyPoly(p: Poly, s: Step): Poly {
  switch (s.op) {
    case 'add':
      return polyAdd(p, operandPoly(s.operand));
    case 'sub':
      return polyAdd(p, polyNeg(operandPoly(s.operand)));
    case 'mul':
      return polyMul(p, operandPoly(s.operand));
    case 'div':
      return trim(p.map((x) => div(x, frac(s.k))));
    case 'square':
      return polyMul(p, p);
  }
}

export function evalPoly(p: Poly, x: Frac): Frac {
  let r = ZERO;
  for (let i = p.length - 1; i >= 0; i--) r = add(mul(r, x), p[i]!);
  return r;
}

/** Les formes réduites après chaque étape (indice 0 : x). */
export function runPoly(steps: readonly Step[]): Poly[] {
  const out: Poly[] = [[ZERO, frac(1)]];
  for (const s of steps) out.push(applyPoly(out[out.length - 1]!, s));
  return out;
}

/** Les nombres obtenus après chaque étape (indice 0 : le nombre de départ). */
export function runNumber(steps: readonly Step[], x: Frac): Frac[] {
  return runPoly(steps).map((p) => evalPoly(p, x));
}

// ── écritures ──────────────────────────────────────────────────────────────

const MULT: Record<number, string> = { 2: 'le double', 3: 'le triple', 4: 'le quadruple' };

/** « Ajouter 3 », « Soustraire le double du nombre de départ », « Élever au carré » */
export function stepLabel(s: Step): string {
  if (s.op === 'square') return 'Élever au carré';
  if (s.op === 'div') return `Diviser par ${num(s.k)}`;
  const verb = { add: 'Ajouter', sub: 'Soustraire', mul: 'Multiplier par' }[s.op];
  const o = s.operand;
  if (o.kind === 'const') return `${verb} ${num(o.k)}`;
  if (o.kind === 'x2') return `${verb} le carré du nombre de départ`;
  if (o.k === 1) return `${verb} le nombre de départ`;
  return `${verb} ${MULT[o.k] ?? `${num(o.k)} fois`} du nombre de départ`.replace(
    / fois du /,
    ' fois le ',
  );
}

/**
 * court, pour la flèche de la machine : « + 3 », « × 2 », « − 2x », « ( )² ».
 * Sans lettres (6e) : « − 2 × départ ».
 */
export function stepShort(s: Step, letters = true): string {
  if (s.op === 'square') return '( )²';
  if (s.op === 'div') return `÷ ${num(s.k)}`;
  const sym = { add: '+', sub: '−', mul: '×' }[s.op];
  const o = s.operand;
  const x = letters ? 'x' : 'départ';
  const v =
    o.kind === 'const'
      ? num(o.k)
      : o.kind === 'x2'
        ? `${x}²`
        : o.k === 1
          ? x
          : letters
            ? `${num(o.k)}x`
            : `${num(o.k)} × départ`;
  return `${sym} ${v}`;
}

/** une étape utilise-t-elle un carré (élever au carré, × le nombre de départ, son carré) ? */
export function usesSquare(s: Step): boolean {
  return (
    s.op === 'square' ||
    (s.op !== 'div' && (s.operand.kind === 'x2' || (s.op === 'mul' && s.operand.kind === 'x')))
  );
}

export const MAX_DEGREE = 4;
const LIMIT = 1e9;

/** fraction dont numérateur et dénominateur restent des entiers exacts (≤ 2⁵³) */
export function safeFrac(f: Frac): boolean {
  return Number.isSafeInteger(f.p) && Number.isSafeInteger(f.q);
}

/** Le programme reste-t-il calculable exactement (degré ≤ 4, coefficients raisonnables) ? */
export function fits(steps: readonly Step[]): boolean {
  return runPoly(steps).every(
    (p) =>
      degree(p) <= MAX_DEGREE &&
      p.every((c) => safeFrac(c) && Math.abs(c.p) <= LIMIT && c.q <= LIMIT),
  );
}

function num(k: number): string {
  return String(k).replace('.', ',').replace('-', '−');
}

function fracTex(f: Frac): string {
  const n = Math.abs(f.p);
  return f.q === 1 ? String(n) : `\\frac{${n}}{${f.q}}`;
}

function fracPlain(f: Frac): string {
  const n = Math.abs(f.p);
  return f.q === 1 ? String(n) : `${n}/${f.q}`;
}

const POW_TEX = (k: number) => (k === 0 ? '' : k === 1 ? 'x' : `x^{${k}}`);
const SUP = '⁰¹²³⁴⁵⁶⁷⁸⁹';
const POW_PLAIN = (k: number) =>
  k === 0
    ? ''
    : k === 1
      ? 'x'
      : `x${String(k)
          .split('')
          .map((d) => SUP[Number(d)])
          .join('')}`;

/** Forme réduite en LaTeX : « 2x^{2} - \frac{1}{2}x + 3 » */
export function polyTex(p: Poly): string {
  return polyWrite(p, fracTex, POW_TEX, '-');
}

/** Forme réduite en texte : « 2x² − 1/2x + 3 » */
export function polyPlain(p: Poly): string {
  return polyWrite(p, fracPlain, POW_PLAIN, '−');
}

function polyWrite(
  p: Poly,
  coef: (f: Frac) => string,
  pow: (k: number) => string,
  minus: string,
): string {
  const t = trim(p);
  const parts: string[] = [];
  for (let k = t.length - 1; k >= 0; k--) {
    const f = t[k]!;
    if (f.p === 0) continue;
    const one = Math.abs(f.p) === 1 && f.q === 1;
    const body = k > 0 && one ? pow(k) : `${coef(f)}${pow(k)}`;
    if (parts.length === 0) parts.push(f.p < 0 ? `${minus}${body}` : body);
    else parts.push(f.p < 0 ? `${minus} ${body}` : `+ ${body}`);
  }
  return parts.length ? parts.join(' ') : '0';
}

/** Atomes de la feuille : 2x² − 3x + 1/2 → ['2','x','²','−','3','x','+','1','÷','2'] */
export function polyAtoms(p: Poly): string[] {
  const t = trim(p);
  const out: string[] = [];
  for (let k = t.length - 1; k >= 0; k--) {
    const f = t[k]!;
    if (f.p === 0) continue;
    if (out.length > 0) out.push(f.p < 0 ? '−' : '+');
    else if (f.p < 0) out.push('−');
    const n = Math.abs(f.p);
    const vars = k === 0 ? [] : k === 1 ? ['x'] : k === 2 ? ['x', '²'] : ['x', '^', String(k)];
    if (!(n === 1 && f.q === 1 && k > 0)) out.push(String(n));
    out.push(...vars);
    if (f.q !== 1) out.push('÷', String(f.q));
  }
  return out.length ? out : ['0'];
}

/** une expression a-t-elle besoin de parenthèses pour être multipliée ? */
function isSum(p: Poly): boolean {
  return trim(p).filter((f) => f.p !== 0).length > 1;
}
function isNegativeMonomial(p: Poly): boolean {
  const nz = trim(p).filter((f) => f.p !== 0);
  return nz.length === 1 && nz[0]!.p < 0;
}

function operandTex(o: Operand): string {
  if (o.kind === 'const') return o.k < 0 ? `(${num(o.k).replace('−', '-')})` : num(o.k);
  if (o.kind === 'x2') return 'x^{2}';
  return o.k === 1 ? 'x' : `${num(o.k)}x`;
}

/**
 * L'expression écrite à cette étape à partir de la forme réduite précédente,
 * AVANT réduction : « 2(x + 3) », « (x + 1)^{2} - x^{2} », « \frac{4x + 8}{2} ».
 */
export function stepTex(prev: Poly, s: Step): string {
  const e = polyTex(prev);
  const wrapped = isSum(prev) || isNegativeMonomial(prev) ? `(${e})` : e;
  switch (s.op) {
    case 'add':
      return `${e} + ${operandTex(s.operand)}`;
    case 'sub':
      return `${e} - ${operandTex(s.operand)}`;
    case 'mul':
      if (s.operand.kind === 'const')
        return wrapped !== e
          ? `${operandTex(s.operand)}${wrapped}`
          : `${operandTex(s.operand)} \\times ${e}`;
      return `${wrapped} \\times ${operandTex(s.operand)}`;
    case 'div':
      return `\\frac{${e}}{${num(s.k)}}`;
    case 'square':
      return e === 'x' ? 'x^{2}' : `(${e})^{2}`;
  }
}

/** Deux programmes : même résultat pour tout x, pour une seule valeur, ou à résoudre. */
export function compare(
  a: Poly,
  b: Poly,
): { kind: 'always' } | { kind: 'never' } | { kind: 'once'; x: Frac } | { kind: 'other' } {
  const d = polyAdd(a, polyNeg(b));
  const deg = degree(d);
  if (deg === -Infinity) return { kind: 'always' };
  if (deg === 0) return { kind: 'never' };
  if (deg === 1) return { kind: 'once', x: div(frac(-d[0]!.p, d[0]!.q), d[1]!) };
  return { kind: 'other' };
}

/**
 * Remonter le programme : le nombre de départ qui donne `target`. Pas à pas
 * (opérations réciproques dans l'ordre inverse) si chaque étape n'utilise
 * qu'un nombre et est réversible ; sinon, par l'équation (forme réduite de
 * degré 1). null si impossible ou hors de portée (degré 2…).
 */
export function reverse(
  steps: readonly Step[],
  target: Frac,
):
  | { how: 'steps'; chain: Array<{ label: string; value: Frac }>; start: Frac }
  | { how: 'equation'; start: Frac }
  | { how: 'none'; why: 'square' | 'constant' | 'degree' } {
  const stepwise = steps.every(
    (s) =>
      s.op === 'div' ||
      (s.op !== 'square' && s.operand.kind === 'const' && !(s.op === 'mul' && s.operand.k === 0)),
  );
  if (stepwise) {
    let v = target;
    const chain: Array<{ label: string; value: Frac }> = [];
    for (let i = steps.length - 1; i >= 0; i--) {
      const s = steps[i]!;
      let label: string;
      if (s.op === 'div') {
        v = mul(v, frac(s.k));
        label = `× ${num(s.k)}`;
      } else if (s.op === 'square') {
        throw new Error('inaccessible');
      } else {
        const k = (s.operand as { k: number }).k;
        if (s.op === 'add') {
          v = sub(v, frac(k));
          label = `− ${num(k)}`;
        } else if (s.op === 'sub') {
          v = add(v, frac(k));
          label = `+ ${num(k)}`;
        } else {
          v = div(v, frac(k));
          label = `÷ ${num(k)}`;
        }
      }
      chain.push({ label, value: v });
    }
    return { how: 'steps', chain, start: v };
  }
  const p = runPoly(steps)[steps.length]!;
  const deg = degree(p);
  if (steps.some((s) => s.op === 'square') && deg >= 2) return { how: 'none', why: 'square' };
  if (deg <= 0) return { how: 'none', why: 'constant' };
  if (deg > 1) return { how: 'none', why: 'degree' };
  return { how: 'equation', start: div(sub(target, p[0]!), p[1]!) };
}
